/* =====================================================
   HARVES — Main JavaScript
   Carousel · Accordion · Counter · Calculator · Nav
   ===================================================== */
'use strict';

/* ─── Scroll Observer (fade-up) ─────────────────── */
function initScrollAnimations() {
  const els = document.querySelectorAll('.hrv-fade-up');
  if (!els.length) return;
  const io = new IntersectionObserver(
    (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } }),
    { rootMargin: '-60px', threshold: 0.1 }
  );
  els.forEach(el => io.observe(el));
}

/* ─── Counter Animation ─────────────────────────── */
function animateCounter(el, to, suffix, duration) {
  const start = performance.now();
  const fmt = (n) => n.toLocaleString('de-DE') + (suffix || '');
  el.textContent = fmt(0);
  const tick = (now) => {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = fmt(Math.round(to * eased));
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function initCounters() {
  const counters = document.querySelectorAll('[data-counter]');
  if (!counters.length) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const el = e.target;
          const to = parseInt(el.dataset.counter, 10);
          const suffix = el.dataset.suffix || '';
          const duration = parseInt(el.dataset.duration || '1400', 10);
          animateCounter(el, to, suffix, duration);
          io.unobserve(el);
        }
      });
    },
    { rootMargin: '-80px', threshold: 0.2 }
  );
  counters.forEach(el => io.observe(el));
}

/* ─── Nav scroll effect ─────────────────────────── */
function initNav() {
  const nav = document.querySelector('.hrv-nav');
  if (!nav) return;

  const update = () => {
    if (window.scrollY > 40) nav.classList.add('scrolled');
    else nav.classList.remove('scrolled');
  };
  window.addEventListener('scroll', update, { passive: true });
  update();

  const hamburger = document.querySelector('.hrv-nav__hamburger');
  const mobileMenu = document.querySelector('.hrv-nav__mobile');
  const mobileClose = document.querySelector('.hrv-nav__mobile-close');

  if (hamburger && mobileMenu) {
    hamburger.addEventListener('click', () => mobileMenu.classList.add('open'));
  }
  if (mobileClose && mobileMenu) {
    mobileClose.addEventListener('click', () => mobileMenu.classList.remove('open'));
  }
  if (mobileMenu) {
    mobileMenu.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => mobileMenu.classList.remove('open'));
    });
  }
}

/* ─── Use-Case Infographics (desktop tab panel) ──── */
function initUseCaseInfographics() {
  const section = document.querySelector('[data-uci]');
  if (!section) return;

  const tabs = section.querySelectorAll('[data-uci-tab]');
  const panels = section.querySelectorAll('[data-uci-panel]');

  function activate(id) {
    tabs.forEach(t => {
      const active = t.dataset.uciTab === id;
      t.classList.toggle('active', active);
      const barEl = t.querySelector('.hrv-uci__tab-bar');
      const iconEl = t.querySelector('.hrv-uci__tab-icon');
      const accent = t.dataset.accent || '#2f8f5e';
      if (barEl) barEl.style.background = active ? accent : 'transparent';
      if (iconEl) {
        iconEl.style.borderColor = active ? accent : '#e2e8f0';
        iconEl.style.background  = active ? `${accent}20` : '#f8fafc';
        iconEl.style.color       = active ? accent : '#475569';
      }
    });
    panels.forEach(p => {
      p.style.display = p.dataset.uciPanel === id ? '' : 'none';
    });
    // Animate flow items
    const activePanel = section.querySelector(`[data-uci-panel="${id}"]`);
    if (activePanel) animateFlow(activePanel);
  }

  function animateFlow(panel) {
    const items = panel.querySelectorAll('.hrv-uci__flow-item');
    const nodes = panel.querySelectorAll('.hrv-uci__flow-node');
    const cards = panel.querySelectorAll('.hrv-uci__flow-card');
    const accent = panel.dataset.accent || '#2f8f5e';
    const totalMs = items.length * 900 + 600;

    items.forEach((_, i) => {
      if (nodes[i]) { nodes[i].style.borderColor = '#cbd5e1'; nodes[i].style.color = '#94a3b8'; }
      if (cards[i]) { cards[i].style.borderColor = '#e2e8f0'; cards[i].style.background = '#fff'; }
    });

    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const p = (elapsed / totalMs) * items.length;
      items.forEach((_, i) => {
        const reached = p >= i + 0.5;
        if (nodes[i]) {
          nodes[i].style.borderColor = reached ? accent : '#cbd5e1';
          nodes[i].style.color = reached ? accent : '#94a3b8';
        }
        if (cards[i]) {
          cards[i].style.borderColor = reached ? `${accent}55` : '#e2e8f0';
          cards[i].style.background  = reached ? `linear-gradient(90deg, ${accent}10, transparent)` : '#fff';
        }
      });
      if (elapsed < totalMs) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  tabs.forEach(tab => tab.addEventListener('click', () => activate(tab.dataset.uciTab)));
  if (tabs.length) activate(tabs[0].dataset.uciTab);
}

/* ─── Tech Steps Carousel ───────────────────────── */
function initTechSteps() {
  const section = document.querySelector('[data-tech-steps]');
  if (!section) return;

  const btns  = section.querySelectorAll('[data-step-btn]');
  const badge = section.querySelector('[data-step-badge]');
  const title = section.querySelector('[data-step-title]');
  const desc  = section.querySelector('[data-step-desc]');
  const statK = section.querySelector('[data-step-stat-k]');
  const statV = section.querySelector('[data-step-stat-v]');
  const fill  = section.querySelector('[data-step-fill]');
  const steps = JSON.parse(section.dataset.techSteps || '[]');

  let active = 0;
  let timer = null;

  function activate(i) {
    active = i;
    const s = steps[i];
    if (!s) return;

    btns.forEach((b, bi) => {
      const isActive = bi === i;
      b.classList.toggle('active', isActive);
      const iconEl = b.querySelector('.hrv-tech__step-icon');
      if (iconEl) {
        iconEl.style.borderColor = isActive ? `${s.color}aa` : 'rgba(255,255,255,.12)';
        iconEl.style.background  = isActive ? `${s.color}22` : 'rgba(255,255,255,.03)';
        iconEl.style.transform   = isActive ? 'scale(1.08)' : 'scale(1)';
      }
      const nameEl = b.querySelector('.hrv-tech__step-name');
      if (nameEl) nameEl.style.color = isActive ? s.color : '#fff';
    });

    if (badge) { badge.textContent = `Schritt ${i + 1} von ${steps.length}`; badge.style.background = `${s.color}20`; badge.style.borderColor = `${s.color}44`; badge.style.color = s.color; }
    if (title) title.textContent = s.title;
    if (desc)  desc.textContent  = s.desc;
    if (statK) statK.textContent = s.statK;
    if (statV) { statV.textContent = s.statV; statV.style.color = s.color; }
    if (fill)  { fill.style.background = s.color; fill.style.animation = 'none'; fill.offsetWidth; fill.style.animation = ''; }
  }

  const go = (dir) => { active = (active + dir + steps.length) % steps.length; activate(active); resetTimer(); };
  btns.forEach((b, i) => b.addEventListener('click', () => { activate(i); resetTimer(); }));

  const prevBtn = section.querySelector('[data-step-prev]');
  const nextBtn = section.querySelector('[data-step-next]');
  if (prevBtn) prevBtn.addEventListener('click', () => go(-1));
  if (nextBtn) nextBtn.addEventListener('click', () => go(1));

  function resetTimer() {
    clearInterval(timer);
    timer = setInterval(() => go(1), 4500);
  }
  resetTimer();
  activate(0);
}

/* ─── Use Cases Swiper (mobile) ─────────────────── */
function initUseCasesSwiper() {
  if (typeof Swiper === 'undefined') return;
  const el = document.querySelector('.hrv-usecases-swiper');
  if (!el) return;
  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    autoplay: { delay: 4000 },
    pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
    navigation: { nextEl: el.querySelector('.swiper-button-next'), prevEl: el.querySelector('.swiper-button-prev') },
    breakpoints: { 640: { slidesPerView: 2 } },
  });
}

/* ─── User Experience Swiper (mobile) ───────────── */
function initUXSwiper() {
  if (typeof Swiper === 'undefined') return;
  const el = document.querySelector('.hrv-ux-swiper');
  if (!el) return;
  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    autoplay: { delay: 5000 },
    pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
    navigation: { nextEl: el.querySelector('.swiper-button-next'), prevEl: el.querySelector('.swiper-button-prev') },
  });
}

/* ─── Testimonials Swiper (mobile) ──────────────── */
function initTestimonialsSwiper() {
  if (typeof Swiper === 'undefined') return;
  const el = document.querySelector('.hrv-testimonials-swiper');
  if (!el) return;
  new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 20,
    loop: true,
    autoplay: { delay: 6000 },
    pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
    breakpoints: { 768: { slidesPerView: 2 } },
  });
}

/* ─── Testimonial bar animations ─────────────────── */
function initTestimonialBars() {
  const bars = document.querySelectorAll('.hrv-testimonial-bar__fill[data-fill]');
  if (!bars.length) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.width = e.target.dataset.fill + '%';
          io.unobserve(e.target);
        }
      });
    },
    { rootMargin: '-60px', threshold: 0.2 }
  );
  bars.forEach(b => io.observe(b));
}

/* ─── Seasonal Calculator ───────────────────────── */
function initCalculator() {
  const form = document.querySelector('[data-calculator]');
  if (!form) return;

  /* Regional rainfall data (DWD 1991-2020) */
  const REGIONS = [
    { prefixes: ['10','11','12','13','14'], city: 'Berlin',           rainfall: 581, split: { winter:.21, fruehling:.22, sommer:.34, herbst:.23 } },
    { prefixes: ['58','44','45','46','47','48'], city: 'Ennepetal / Ruhr-Sauerland', rainfall: 950, split: { winter:.26, fruehling:.22, sommer:.26, herbst:.26 } },
    { prefixes: ['80','81','82','83','84','85'], city: 'München',     rainfall: 960, split: { winter:.17, fruehling:.24, sommer:.37, herbst:.22 } },
    { prefixes: ['20','21','22'],           city: 'Hamburg',          rainfall: 770, split: { winter:.24, fruehling:.20, sommer:.28, herbst:.28 } },
    { prefixes: ['50','51','52','53'],      city: 'Köln / Bonn',      rainfall: 800, split: { winter:.25, fruehling:.22, sommer:.27, herbst:.26 } },
    { prefixes: ['60','61','62','63','64','65'], city: 'Frankfurt',   rainfall: 640, split: { winter:.22, fruehling:.24, sommer:.30, herbst:.24 } },
    { prefixes: ['01','02','03','04','05','06','07','08','09'], city: 'Dresden / Sachsen', rainfall: 660, split: { winter:.19, fruehling:.23, sommer:.34, herbst:.24 } },
    { prefixes: ['70','71','72','73','74'], city: 'Stuttgart',        rainfall: 700, split: { winter:.21, fruehling:.25, sommer:.32, herbst:.22 } },
    { prefixes: ['28','29'],               city: 'Bremen',            rainfall: 720, split: { winter:.23, fruehling:.21, sommer:.29, herbst:.27 } },
    { prefixes: ['90','91','92','93','94','95','96'], city: 'Nürnberg / Franken', rainfall: 620, split: { winter:.20, fruehling:.24, sommer:.33, herbst:.23 } },
  ];
  const DEFAULT_SPLIT    = { winter:.22, fruehling:.24, sommer:.30, herbst:.24 };
  const DEFAULT_RAINFALL = 790;

  const ROOF_TYPES = {
    ziegel: .80, flach: .80, metall: .80, kies: .60, gruen_ex: .50, gruen_in: .30,
  };
  const FILTER_EFFICIENCY = 0.90;
  const STANDARD_TANKS = [1000,1500,2000,3000,5000,7500,10000];
  const BUNDESLAENDER = {
    bw:2.11, by:1.56, be:1.81, bb:1.54, hb:2.13, hh:1.83, he:2.02,
    mv:1.64, ni:1.26, nw:1.66, rp:1.73, sl:1.90, sn:1.90, st:1.72, sh:1.46, th:2.03,
  };
  const SEASONS = [
    { id:'winter',    label:'Winter',   icon:'❄', color:'#04a9de' },
    { id:'fruehling', label:'Frühling', icon:'✿', color:'#4caf50' },
    { id:'sommer',    label:'Sommer',   icon:'☀', color:'#f26b38' },
    { id:'herbst',    label:'Herbst',   icon:'✦', color:'#b45309' },
  ];

  function findRegion(plz) {
    const p2 = plz.trim().slice(0,2);
    return REGIONS.find(r => r.prefixes.includes(p2)) || null;
  }

  function fmt(n)   { return n.toLocaleString('de-DE', { maximumFractionDigits:1 }); }
  function fmt0(n)  { return n.toLocaleString('de-DE', { maximumFractionDigits:0 }); }
  function fmtEUR(n){ return n.toLocaleString('de-DE', { style:'currency', currency:'EUR', maximumFractionDigits:0 }); }

  function calculate() {
    const plz        = form.querySelector('[name=plz]')?.value || '10115';
    const area       = parseFloat(form.querySelector('[name=area]')?.value) || 150;
    const roofId     = form.querySelector('[name=roof]')?.value || 'ziegel';
    const persons    = parseInt(form.querySelector('[name=persons]')?.value) || 4;
    const gardenArea = parseFloat(form.querySelector('[name=garden]')?.value) || 100;
    const useWC      = form.querySelector('[name=use_wc]')?.checked ?? true;
    const useWM      = form.querySelector('[name=use_wm]')?.checked ?? true;
    const useGarden  = form.querySelector('[name=use_garden]')?.checked ?? true;
    const blId       = form.querySelector('[name=bundesland]')?.value || 'be';
    const abwasser   = parseFloat(form.querySelector('[name=abwasser]')?.value) || 2.50;

    const region   = findRegion(plz);
    const cityLabel= region?.city || 'Deutschland-Ø';
    const rainfall = region?.rainfall || DEFAULT_RAINFALL;
    const split    = region?.split || DEFAULT_SPLIT;
    const roofEff  = ROOF_TYPES[roofId] || .80;
    const effective= roofEff * FILTER_EFFICIENCY;
    const freshPrice = BUNDESLAENDER[blId] || 1.81;
    const pricePerM3 = freshPrice + abwasser;

    const yearlyM3 = area * rainfall * effective / 1000;
    const yearlyL  = yearlyM3 * 1000;

    const demandWC  = useWC  ? 8.76 * persons : 0;
    const demandWM  = useWM  ? 4.4  * persons : 0;
    const demandGar = useGarden ? 0.06 * gardenArea : 0;
    const demandM3  = demandWC + demandWM + demandGar;
    const usedM3    = Math.min(yearlyM3, demandM3);
    const coverage  = demandM3 > 0 ? Math.min(100, (usedM3 / demandM3) * 100) : 0;
    const savings   = usedM3 * pricePerM3;

    const baseTankL = Math.max(1000, Math.min(yearlyM3, demandM3) * 0.06 * 1.10 * 1000);
    const tankSize  = STANDARD_TANKS.find(t => t >= baseTankL) || STANDARD_TANKS[STANDARD_TANKS.length-1];

    /* Update city hint */
    const cityHint = form.querySelector('[data-city-hint]');
    if (cityHint) cityHint.textContent = region ? `📍 ${cityLabel} · ${fmt0(rainfall)} mm/Jahr` : `⚠ Unbekannte PLZ → Ø Deutschland ${DEFAULT_RAINFALL} mm`;

    const effHint = form.querySelector('[data-eff-hint]');
    if (effHint) effHint.textContent = `Ertrag ${roofEff.toFixed(2)} × Filter ${FILTER_EFFICIENCY.toFixed(2)} = ${effective.toFixed(2)}`;

    const freshHint = form.querySelector('[data-fresh-hint]');
    if (freshHint) freshHint.textContent = `Frischwasser: ${fmt(freshPrice)} €/m³`;

    const totalPrice = form.querySelector('[data-total-price]');
    if (totalPrice) { totalPrice.textContent = `${fmt(pricePerM3)} €/m³`; }

    /* Seasonal */
    const maxS = Math.max(...Object.values(split).map(v => yearlyM3 * v), 0.001);
    SEASONS.forEach(s => {
      const m3 = yearlyM3 * split[s.id];
      const pct = (m3 / maxS) * 100;
      const el = form.querySelector(`[data-season="${s.id}"]`);
      if (!el) return;
      const valEl  = el.querySelector('[data-season-val]');
      const subEl  = el.querySelector('[data-season-sub]');
      const pctEl  = el.querySelector('[data-season-pct]');
      const barEl  = el.querySelector('[data-season-bar]');
      if (valEl)  valEl.textContent = fmt(m3);
      if (subEl)  subEl.textContent = `m³ · ${fmt0(m3*1000)} L`;
      if (pctEl)  pctEl.textContent = `${Math.round(split[s.id]*100)} %`;
      if (barEl)  { setTimeout(() => { barEl.style.width = pct + '%'; }, 50); }
    });

    /* Summary */
    const sYearly  = form.querySelector('[data-sum-yearly]');
    const sDemand  = form.querySelector('[data-sum-demand]');
    const sTank    = form.querySelector('[data-sum-tank]');
    const sCoverage= form.querySelector('[data-sum-coverage]');
    if (sYearly)   sYearly.textContent   = `${fmt(yearlyM3)} m³`;
    if (sDemand)   sDemand.textContent   = `${fmt(demandM3)} m³`;
    if (sTank)     sTank.textContent     = `${fmt0(tankSize)} L`;
    if (sCoverage) sCoverage.textContent = `${fmt0(coverage)} %`;

    /* Savings */
    const savingsEl = form.querySelector('[data-savings-val]');
    const progressEl= form.querySelector('[data-progress-fill]');
    const savingsDesc=form.querySelector('[data-savings-desc]');
    if (savingsEl)  savingsEl.textContent = fmtEUR(savings);
    if (progressEl) { setTimeout(() => { progressEl.style.width = coverage + '%'; }, 50); }
    if (savingsDesc) savingsDesc.textContent = `Sammeln ${fmt(yearlyM3)} m³ · Bedarf ${fmt(demandM3)} m³ · Genutzt ${fmt(usedM3)} m³ (${fmt0(coverage)} %). Ersparnis basiert nur auf tatsächlich genutztem Wasser.`;

    const warningEl = form.querySelector('[data-savings-warning]');
    if (warningEl) warningEl.style.display = yearlyM3 < demandM3 ? '' : 'none';

    /* Store for PDF */
    form._calcData = { plz, cityLabel, rainfall, area, roofId, roofEff, effective, persons, gardenArea, useWC, useWM, useGarden, blId, freshPrice, abwasser, pricePerM3, yearlyM3, yearlyL, demandM3, usedM3, coverage, savings, tankSize, split };
  }

  /* Range sync */
  const rangeArea = form.querySelector('[name=area_range]');
  const inputArea = form.querySelector('[name=area]');
  if (rangeArea && inputArea) {
    rangeArea.addEventListener('input', () => { inputArea.value = rangeArea.value; calculate(); });
    inputArea.addEventListener('input', () => { rangeArea.value = inputArea.value; calculate(); });
  }

  form.addEventListener('input', calculate);
  form.addEventListener('change', calculate);

  /* PDF Report */
  const pdfBtn = form.querySelector('[data-pdf-btn]');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', () => {
      const d = form._calcData;
      if (!d) return;
      const fmt2 = (n) => n.toLocaleString('de-DE', { maximumFractionDigits:1 });
      const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Harves Report · ${d.cityLabel}</title>
<style>*{box-sizing:border-box}body{font-family:system-ui,sans-serif;max-width:760px;margin:40px auto;padding:0 24px;color:#1a1a1a;line-height:1.5}h1{font-size:28px;letter-spacing:-.02em;margin:0 0 4px}h2{font-size:13px;text-transform:uppercase;letter-spacing:.14em;margin:28px 0 8px;color:#555}.sub{color:#666;font-size:13px;margin-bottom:32px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:16px 0}.box{border:1px solid #e5e5e5;border-radius:12px;padding:14px}.lbl{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#666;font-weight:700}.val{font-size:20px;font-weight:800;margin-top:4px}.accent{color:#4caf50}table{width:100%;border-collapse:collapse;margin:8px 0 24px}th,td{padding:10px 12px;text-align:left;border-bottom:1px solid #eee;font-size:14px}th{font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:#666}.foot{margin-top:40px;padding-top:16px;border-top:1px solid #eee;font-size:11px;color:#999}@media print{body{margin:0}}</style></head><body>
<h1>Regenwasser-Potenzial · ${d.cityLabel}</h1>
<div class="sub">Harves Wassermanagement · ${new Date().toLocaleDateString('de-DE')} · DIN 1989-100:2022-07</div>
<h2>Ergebnis</h2>
<div class="grid">
<div class="box"><div class="lbl">Jahresertrag</div><div class="val">${fmt2(d.yearlyM3)} m³</div></div>
<div class="box"><div class="lbl">Jahresbedarf</div><div class="val">${fmt2(d.demandM3)} m³</div></div>
<div class="box"><div class="lbl">Empfohlener Tank</div><div class="val">${d.tankSize.toLocaleString('de-DE')} L</div></div>
<div class="box"><div class="lbl">Deckungsrate</div><div class="val accent">${Math.round(d.coverage)} %</div></div>
<div class="box"><div class="lbl">Jährliche Ersparnis</div><div class="val accent">${d.savings.toLocaleString('de-DE',{style:'currency',currency:'EUR',maximumFractionDigits:0})}</div></div>
</div>
<div class="foot">© Harves Technology · DWD 1991–2020</div>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
</body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      const w    = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = `harves-report-${d.cityLabel}.html`; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    });
  }

  calculate();
}

/* ─── Gallery lightbox (simple) ─────────────────── */
function initGallery() {
  const items = document.querySelectorAll('.hrv-gallery-item img');
  if (!items.length) return;

  const overlay = document.createElement('div');
  overlay.style.cssText = 'display:none;position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.9);align-items:center;justify-content:center;cursor:pointer;';
  const img = document.createElement('img');
  img.style.cssText = 'max-width:90vw;max-height:90vh;border-radius:16px;';
  overlay.appendChild(img);
  document.body.appendChild(overlay);

  items.forEach(el => {
    el.style.cursor = 'pointer';
    el.addEventListener('click', () => {
      img.src = el.src;
      overlay.style.display = 'flex';
    });
  });
  overlay.addEventListener('click', () => { overlay.style.display = 'none'; });
}

/* ─── Contact form GDPR gate ─────────────────────── */
function initContactForm() {
  const form = document.querySelector('.hrv-contact__form form');
  if (!form) return;
  const submitBtn = form.querySelector('.hrv-contact__submit');
  const gdpr      = form.querySelector('[name=gdpr]');
  if (!gdpr || !submitBtn) return;
  const update = () => { submitBtn.disabled = !gdpr.checked; };
  gdpr.addEventListener('change', update);
  update();
}

/* ─── Hero slide indicators ──────────────────────── */
function initHeroSlides() {
  const hero = document.querySelector('.hrv-hero');
  if (!hero) return;
  const bg   = hero.querySelector('.hrv-hero__bg img');
  const dots  = hero.querySelectorAll('.hrv-hero__dot');
  if (!dots.length) return;

  const slides = JSON.parse(hero.dataset.slides || '[]');
  if (!slides.length) return;

  let current = 0;
  let timer = null;

  function activate(i) {
    current = i;
    dots.forEach((d, di) => d.classList.toggle('active', di === i));
    if (bg && slides[i]?.img) bg.src = slides[i].img;
    const h1 = hero.querySelector('.hrv-hero__h1');
    if (h1 && slides[i]?.title) h1.innerHTML = slides[i].title;
  }

  dots.forEach((d, i) => d.addEventListener('click', () => { activate(i); resetTimer(); }));
  function resetTimer() {
    clearInterval(timer);
    timer = setInterval(() => activate((current + 1) % slides.length), 6000);
  }
  resetTimer();
}

/* ─── Lazy Swiper init via IntersectionObserver ─── */
function initSwipersLazy() {
  if (typeof Swiper === 'undefined') {
    window.addEventListener('swiper-ready', initSwipersLazy, { once: true });
    return;
  }

  const targets = [
    { el: '.hrv-usecases-swiper', fn: initUseCasesSwiper },
    { el: '.hrv-ux-swiper',       fn: initUXSwiper },
    { el: '.hrv-testimonials-swiper', fn: initTestimonialsSwiper },
  ];

  targets.forEach(({ el, fn }) => {
    const node = document.querySelector(el);
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { fn(); io.disconnect(); } });
    }, { rootMargin: '200px' });
    io.observe(node);
  });
}

/* ─── Init all ──────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initCounters();
  initNav();
  initUseCaseInfographics();
  initTechSteps();
  initTestimonialBars();
  initGallery();
  initContactForm();
  initHeroSlides();
  initCalculator();
  initSwipersLazy();
});

/* Notify section-level inline scripts that Swiper is available.
   Defer scripts run after full HTML parse → all swiper-ready listeners
   are already registered by the time this fires. */
window.dispatchEvent(new CustomEvent('swiper-ready'));
